import React, { useMemo, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  Tooltip,
  Typography,
  Box,
  IconButton,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useRecordContext } from "react-admin";

const VIDEO_PREVIEW_MESSAGE =
  "Por favor, faca o download da midia. Esta e apenas uma pre-visualizacao limitada.";

const VideoPlayer = React.forwardRef(({ src, preview = false }, ref) => (
  <video
    ref={ref}
    controls
    preload="metadata"
    style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
    src={src}
    controlsList="nodownload noplaybackrate noremoteplayback"
    onTimeUpdate={(event) => {
      if (!preview) return;
      const element = event.currentTarget;
      if (!element.duration) return;
      const maxTime = element.duration * 0.3;
      if (element.currentTime >= maxTime) {
        element.pause();
        element.currentTime = 0;
      }
    }}
  />
));

VideoPlayer.displayName = "VideoPlayer";

const ControlVideo = () => {
  const record = useRecordContext();
  if (!record || record.media_class !== "Video" || !record.url) return null;
  return <VideoPlayer src={record.url} />;
};

const ControlVideoPreview = () => {
  const [open, setOpen] = useState(false);
  const videoRef = useRef(null);
  const record = useRecordContext();

  const previewUrl = useMemo(() => {
    if (!record || record.media_class !== "Video" || !record.url) return null;
    return record.url;
  }, [record]);

  if (!previewUrl) return null;

  const stopPropagation = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleOpen = (event) => {
    stopPropagation(event);
    setOpen(true);
  };

  const handleClose = (event) => {
    if (event) stopPropagation(event);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    setOpen(false);
  };

  return (
    <>
      <Tooltip title={VIDEO_PREVIEW_MESSAGE}>
        <Button
          variant="contained"
          size="small"
          onClick={handleOpen}
          startIcon={<VisibilityOutlinedIcon />}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 700,
            px: 1.25,
            color: "#0f172a",
            background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
            border: "1px solid rgba(15,23,42,0.12)",
            boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
            "&:hover": {
              background: "linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%)",
              boxShadow: "0 4px 12px rgba(15, 23, 42, 0.12)",
            },
          }}
        >
          Preview
        </Button>
      </Tooltip>

      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        onClose={handleClose}
        onClick={stopPropagation}
        PaperProps={{
          sx: {
            borderRadius: "14px",
            height: { xs: "85vh", md: "78vh" },
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogContent sx={{ flex: 1, p: 0, m: 0, display: "flex", minHeight: 0 }}>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              backgroundColor: "#0f172a",
              overflow: "hidden",
              m: 0,
              position: "relative",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                zIndex: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1,
                py: 0.8,
                background: "linear-gradient(180deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.25) 70%, rgba(0,0,0,0) 100%)",
              }}
            >
              <Typography sx={{ color: "#fff", fontSize: 12, fontWeight: 700 }}>
                Pre-visualizacao limitada. Faca o download para acessar a midia completa.
              </Typography>
              <IconButton
                onClick={handleClose}
                size="small"
                sx={{
                  color: "#fff",
                  backgroundColor: "rgba(0,0,0,0.35)",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.55)" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <VideoPlayer ref={videoRef} src={previewUrl} preview />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ControlVideo;
export { ControlVideoPreview };
